import { useCallback, type KeyboardEvent, type MouseEvent } from 'react'

/** One tab or section: its id and visible label. */
export interface TabItem {
  readonly id: string
  readonly label: string
}

/** The ids that link a tab to its panel, under `idPrefix`; none without one. */
export function tabIds(idPrefix: string | undefined, tabId: string) {
  return idPrefix === undefined
    ? {}
    : { id: `${idPrefix}-tab-${tabId}`, 'aria-controls': `${idPrefix}-panel-${tabId}` }
}

/**
 * Props for the panel a `Tabs` or `SectionNav` given the same `idPrefix` controls, so the two
 * are linked for assistive technology.
 */
export function tabPanelProps(idPrefix: string, tabId: string) {
  return {
    id: `${idPrefix}-panel-${tabId}`,
    role: 'tabpanel',
    'aria-labelledby': `${idPrefix}-tab-${tabId}`,
    // The WAI-ARIA tabs pattern: Tab reaches the panel even when its first content is not focusable.
    tabIndex: 0,
  } as const
}

/**
 * Click and key handlers for tabs that carry `data-tab-id`. Arrow keys move to the next or
 * previous tab, Home and End to the first and last, and the tab moved to is selected and
 * focused: the WAI-ARIA tabs pattern, with automatic activation.
 */
export function useTabHandlers(
  tabs: readonly TabItem[],
  onChange: (id: string) => void,
  orientation: 'horizontal' | 'vertical',
) {
  const onClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const id = event.currentTarget.dataset['tabId']
      if (id !== undefined) {
        onChange(id)
      }
    },
    [onChange],
  )

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const previous = orientation === 'horizontal' ? 'ArrowLeft' : 'ArrowUp'
      const next = orientation === 'horizontal' ? 'ArrowRight' : 'ArrowDown'
      const current = tabs.findIndex((tab) => tab.id === event.currentTarget.dataset['tabId'])
      let target: number
      switch (event.key) {
        case previous:
          target = (current - 1 + tabs.length) % tabs.length
          break
        case next:
          target = (current + 1) % tabs.length
          break
        case 'Home':
          target = 0
          break
        case 'End':
          target = tabs.length - 1
          break
        default:
          return
      }
      event.preventDefault()
      const tab = tabs[target]
      if (tab === undefined) {
        return
      }
      onChange(tab.id)
      const list = event.currentTarget.closest('[role="tablist"]')
      list?.querySelectorAll<HTMLElement>('[role="tab"]')[target]?.focus()
    },
    [tabs, onChange, orientation],
  )

  return { onClick, onKeyDown }
}
